import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../Buttons/Button";

const DropdownSelect = ({ title, options = [], values = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [alignRight, setAlignRight] = useState(false);
  const getId = (o) => {
    const v = o && o.value;
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      return String(v);
    if (o && o.id != null) return String(o.id);
    return String(o?.label ?? "");
  };
  const [selectedValues, setSelectedValues] = useState(() => {
    const ids = options.map(getId);
    const initial = values.length ? values.map((v) => String(v)) : [];
    return Array.from(new Set(initial.filter((id) => ids.includes(id))));
  });
  const [query, setQuery] = useState("");
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (
        open &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [open]);
  useEffect(() => {
    const computePosition = () => {
      if (!open || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownWidth =
        dropdownRef.current?.offsetWidth ?? Math.max(containerRect.width, 208);
      const willOverflowRight =
        containerRect.left + dropdownWidth > window.innerWidth;
      setAlignRight(willOverflowRight);
    };
    if (open) {
      requestAnimationFrame(computePosition);
      window.addEventListener("resize", computePosition);
    }
    return () => {
      window.removeEventListener("resize", computePosition);
    };
  }, [open]);
  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );
  const optionIds = options.map(getId);
  const effectiveSelected = Array.from(
    new Set(selectedValues.filter((id) => optionIds.includes(id)))
  );
  const toggleValue = (val) => {
    setSelectedValues((prev) => {
      const base = Array.from(
        new Set(prev.filter((id) => optionIds.includes(id)))
      );
      const exists = base.includes(val);
      const next = exists ? base.filter((id) => id !== val) : [...base, val];
      const uniqueNext = Array.from(new Set(next));
      if (onChange) onChange(uniqueNext);
      return uniqueNext;
    });
  };
  const selectAll = () => {
    const next = effectiveSelected.length === optionIds.length ? [] : optionIds;
    setSelectedValues(next);
    if (onChange) onChange(next);
  };
  const clearAll = () => {
    setSelectedValues([]);
    if (onChange) onChange([]);
  };
  return (
    <div
      ref={containerRef}
      className="p-2 px-3 bg-neutrals-50 rounded relative"
    >
      <div
        className="text-xs text-neutral-500 cursor-pointer select-none"
        onClick={() => setOpen(!open)}
      >
        {title}: {effectiveSelected.length}/{options.length}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className={`absolute top-full ${
              alignRight ? "right-0" : "left-0"
            } min-w-52 max-h-64 mt-1 overflow-hidden bg-white border border-neutrals-100 shadow rounded text-xs text-neutral-500 z-50 flex flex-col gap-2`}
          >
            <div className="p-2 pb-0">
              <input
                type="text"
                className="w-full p-2 border border-neutral-200 rounded"
                placeholder="Filter..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="flex-1 overflow-auto px-2">
              {filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`p-2 text-nowrap cursor-pointer hover:bg-primary-50 flex items-center gap-1 ${
                    effectiveSelected.includes(getId(option))
                      ? "text-neutrals-800"
                      : "text-neutrals-500"
                  }`}
                  onClick={() => toggleValue(getId(option))}
                >
                  <input
                    type="checkbox"
                    checked={effectiveSelected.includes(getId(option))}
                    onChange={() => toggleValue(getId(option))}
                    className="w-3 h-3 rounded border border-neutrals-50"
                  />
                  {option.label}
                </div>
              ))}
            </div>
            <div className="p-2 flex gap-3 bg-neutrals-50">
              <Button
                title="Select All"
                onClick={selectAll}
                size="sm"
                className="px-2!"
              />
              <Button
                title="Clear"
                variant="outlined"
                onClick={clearAll}
                size="sm"
                className="px-2!"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropdownSelect;
